import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Eye, Pencil, Ban, Repeat, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { money, fmtDate, todayISO } from "@/lib/format";
import { useApp } from "@/context/AppCtx";
import { PageHeader, StatusBadge, Empty, Confirm, Kpi } from "@/components/kit";
import { PayDialog } from "@/components/PayDialog";

const STATUSES = { quote: ["vigente", "convertida", "anulada"], invoice: ["pendiente", "pagada", "anulada"] };

// Listado de proformas o facturas (también usado para Cuentas por cobrar)
export default function DocList({ kind, receivables }) {
  const { base, settings } = useApp();
  const nav = useNavigate();
  const isQ = kind === "quote";
  const path = isQ ? "quotes" : "invoices";
  const seg = isQ ? "proformas" : "facturas";
  const [list, setList] = useState([]);
  const [clients, setClients] = useState([]);
  const [f, setF] = useState({ q: "", status: receivables ? "pendiente" : "all", client_id: "all", date_from: "", date_to: "" });
  const [payInv, setPayInv] = useState(null);

  const load = useCallback(() => {
    const params = { ...f, status: f.status === "all" ? "" : f.status, client_id: f.client_id === "all" ? "" : f.client_id };
    return api.get(`/${path}`, { params }).then((r) => setList(r.data));
  }, [f, path]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);
  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
  }, []);

  const act = async (url, msg, after) => {
    try {
      const { data } = await api.post(url, after === "status" ? { status: "anulada" } : undefined);
      toast.success(msg);
      if (after === "goto") {
        nav(`${base}/facturas/${data.id}`, { state: { autoSend: settings.auto_email !== false } });
      }
      else load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const crc = (d, k) => (d.currency === "USD" ? d[k] * (d.exchange_rate || 1) : d[k]);
  const sumCRC = list.filter((d) => d.status !== "anulada").reduce((s, d) => s + crc(d, "total"), 0);
  const sumBal = isQ ? 0 : list.reduce((s, d) => s + crc(d, "balance"), 0);
  const today = todayISO();
  const title = receivables ? "Cuentas por cobrar" : isQ ? "Proformas" : "Facturas";

  return (
    <div>
      <PageHeader title={title} subtitle={receivables ? "Facturas pendientes de pago" : isQ ? "Cotizaciones para clientes" : "Historial de facturación"}>
        <Button className="btn-neon" onClick={() => nav(`${base}/${seg}/nueva`)} data-testid="new-doc-btn">
          <Plus className="w-4 h-4 mr-1" />{isQ ? "Nueva proforma" : "Nueva factura"}
        </Button>
      </PageHeader>

      {receivables && (
        <div className="grid sm:grid-cols-3 gap-4 mb-4">
          <Kpi testId="cxc-total" label="Total por cobrar" value={money(sumBal)} />
          <Kpi testId="cxc-count" label="Facturas pendientes" value={list.length} accent="#F59E0B" />
          <Kpi testId="cxc-overdue" label="Vencidas" value={list.filter((d) => d.due_date && d.due_date < today).length} accent="#F43F5E" />
        </div>
      )}

      <div className="panel p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
        <div className="relative lg:col-span-2">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-500" />
          <Input className="pl-9" placeholder="Buscar por número o cliente" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="doc-search-input" />
        </div>
        <Select value={f.client_id} onValueChange={(v) => setF({ ...f, client_id: v })}>
          <SelectTrigger data-testid="doc-client-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los clientes</SelectItem>
            {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {!receivables && (
          <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
            <SelectTrigger data-testid="doc-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              {STATUSES[kind].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <div className="flex gap-2">
          <Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} data-testid="doc-date-from" title="Desde" />
          <Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="doc-date-to" title="Hasta" />
        </div>
      </div>

      <div className="panel overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]" data-testid="docs-table">
          <thead>
            <tr className="text-left label-cap">
              <th className="p-4">Número</th><th>Cliente</th><th>Emisión</th><th>{isQ ? "Vigencia" : "Vence"}</th>
              <th className="text-right">Total</th>{!isQ && <th className="text-right">Saldo</th>}<th className="pl-4">Estado</th><th className="text-right pr-4">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {list.map((d) => {
              const overdue = !isQ && d.status === "pendiente" && d.due_date < today;
              return (
                <tr key={d.id} className="border-t border-[#271E56] hover:bg-[#1A143D]/60" data-testid={`doc-row-${d.number}`}>
                  <td className="p-4"><Link to={`${base}/${seg}/${d.id}`} className="mono text-[#00F0FF] hover:underline">{d.number}</Link>
                    {d.invoice_number && <div className="text-xs text-violet-300 mono">→ {d.invoice_number}</div>}
                    {d.quote_number && <div className="text-xs text-slate-500 mono">de {d.quote_number}</div>}
                  </td>
                  <td className="text-white">{d.client?.name || "Cliente contado"}</td>
                  <td className="text-slate-400">{fmtDate(d.issue_date)}</td>
                  <td className={overdue ? "text-rose-400 font-semibold" : "text-slate-400"}>{fmtDate(isQ ? d.valid_until : d.due_date)}{overdue && " · vencida"}</td>
                  <td className="mono text-right text-white">{money(d.total, d.currency)}</td>
                  {!isQ && (
                    <td className={`mono text-right ${d.balance > 0 ? "text-amber-300" : "text-slate-500"}`} data-testid={`doc-balance-${d.number}`}>
                      {money(d.balance, d.currency)}
                      {d.balance > 0 && d.paid_amount > 0 && <div className="text-[11px] text-emerald-400">abonado {money(d.paid_amount, d.currency)}</div>}
                    </td>
                  )}
                  <td className="pl-4"><StatusBadge status={d.status} testId={`doc-status-${d.number}`} /></td>
                  <td className="pr-4">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" title="Ver / imprimir" onClick={() => nav(`${base}/${seg}/${d.id}`)} data-testid={`doc-view-${d.number}`}><Eye className="w-4 h-4" /></Button>
                      {isQ && d.status === "vigente" && (
                        <>
                          <Button size="icon" variant="ghost" title="Editar" onClick={() => nav(`${base}/proformas/${d.id}/editar`)} data-testid={`doc-edit-${d.number}`}><Pencil className="w-4 h-4" /></Button>
                          <Confirm title="¿Convertir en factura?" description="Se creará una factura con todos los datos y se descontará el inventario." onConfirm={() => act(`/quotes/${d.id}/convert`, "Factura creada", "goto")} actionLabel="Convertir">
                            <Button size="icon" variant="ghost" className="text-violet-300" title="Convertir en factura" data-testid={`doc-convert-${d.number}`}><Repeat className="w-4 h-4" /></Button>
                          </Confirm>
                          <Confirm title="¿Anular proforma?" onConfirm={() => act(`/quotes/${d.id}/void`, "Proforma anulada")} actionLabel="Anular">
                            <Button size="icon" variant="ghost" className="text-rose-400" title="Anular" data-testid={`doc-void-${d.number}`}><Ban className="w-4 h-4" /></Button>
                          </Confirm>
                        </>
                      )}
                      {!isQ && d.status === "pendiente" && (
                        <Button size="icon" variant="ghost" className="text-emerald-400" title="Registrar abono / pago" onClick={() => setPayInv(d)} data-testid={`doc-pay-${d.number}`}><CheckCircle2 className="w-4 h-4" /></Button>
                      )}
                      {!isQ && d.status !== "anulada" && (
                        <Confirm title="¿Anular factura?" description="El inventario de los productos se devolverá al stock." onConfirm={() => act(`/invoices/${d.id}/status`, "Factura anulada", "status")} actionLabel="Anular">
                          <Button size="icon" variant="ghost" className="text-rose-400" title="Anular" data-testid={`doc-void-${d.number}`}><Ban className="w-4 h-4" /></Button>
                        </Confirm>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {list.length > 0 && (
            <tfoot>
              <tr className="border-t border-[#271E56]">
                <td colSpan={4} className="p-4 label-cap">Total (sin anuladas, en colones)</td>
                <td className="mono text-right text-[#00F0FF] font-bold" data-testid="docs-total">{money(sumCRC)}</td>
                {!isQ && <td className="mono text-right text-amber-300 font-bold" data-testid="docs-balance-total">{money(sumBal)}</td>}
                <td colSpan={2} />
              </tr>
            </tfoot>
          )}
        </table>
        {!list.length && <Empty text="No hay documentos con estos filtros" />}
      </div>
      <PayDialog invoice={payInv} onOpenChange={(o) => !o && setPayInv(null)} onDone={load} />
    </div>
  );
}
